import { createSelectorCreator, defaultMemoize } from 'reselect';
import type { HubState } from '../state';
import {
    AppState,
    SearchState,
    ClickedHubPersonaState,
    FolderLoadingStateMap,
    HubPersonasMap,
    UnifiedGroupsMap,
    SearchQuery,
    getActiveLeftNavItemId,
    getFavorites,
    FolderLoadingState,
    isInSearchMode,
    isInEVNavigationMode,
    isActiveLeftNavItemDeletedItems,
    isInDeepLinkMode,
    getActiveLeftNavItemSparseFolder,
} from '../../appstate';
import {
    HubPersona,
    Person,
    PersonType,
    ContactFolder,
    FavoriteEntity,
    createSelector,
    createMapOfSet,
    isHubPerson,
    favoritesLeftNavItemId,
    isContactsOrContactListsLeftNavItem,
    deletedItemsLeftNavItemId,
    HubListEntryKind,
    rootContactFolderDistinguishedName,
    DeletedItemsSubViewType,
    UnifiedGroup,
    getMatchingFavoriteUnifiedGroup,
    SparseFolderStateMap,
    SparseFolderLoadedState,
} from '../../shared';
import {
    filterPersonasAndRemoveDeleted,
    removeDeletedGroups,
    applyGroupingAndSorting,
    toPersonaListElement,
    isPersonaMapsEqual,
    extractPersonasWithRelevancyScore,
    getPersonaListLoadingState,
} from './personaListSelectorUtils';
import { extractSearchState } from '../utils/searchStateUtils';
import {
    getCurrentFolderPersonas,
    getFavoritePersonas,
    getPersonasByFolderId,
} from './personaListSharedInternalSelectors';
import { getPersonaListViewModel } from './personaListViewModelSelector';
import type { PersonaListViewModel } from '../models/PersonaListViewModel';
import { PersonaListLoadingState } from '../models/personaListLoadingState';
import { mergeAndExpandResults, searchPeopleLocally } from '../../searchbox';
import { compareByGivenRelevancyAndRelevancy } from '../utils/groups/unifiedGroupSorting';

export { HubPersona, Person, SearchState, ClickedHubPersonaState, ContactFolder, SearchQuery };

export type PersonaEntry = { kind: HubListEntryKind.Content; value: HubPersona };

export type PersonaGroup = {
    readonly kind: HubListEntryKind.Group;
    readonly index: string;
    readonly iconName?: string;
    readonly isEmptyRepresentation?: boolean;
};

export type PersonaListEntry = PersonaGroup | PersonaEntry;

export type SparsePersonaListEntries = { [index: number]: PersonaListEntry };

export const getSuggestedPersonas = createSelector<
    AppState,
    HubState,
    HubPersona[],
    HubPersonasMap,
    FavoriteEntity[],
    PersonaListEntry[]
>(
    'getSuggestedPersonas',
    [
        (appState: AppState, _hubState: HubState) =>
            appState.data.hubState.suggestedPersonasState.personas,
        createSelectorCreator(defaultMemoize, isPersonaMapsEqual)(
            appState => appState.data.personas,
            personas => personas
        ),
        getFavorites,
    ],
    (suggestedPersonas, contactsPersonas, favoritePersonas) => {
        const contactsPersonaIds = Object.keys(contactsPersonas)
            .map(key => contactsPersonas[key])
            .filter(persona => persona.type === PersonType.Person && !persona.isReadOnly)
            .map(persona => persona.hubPersonaId);

        const allPersonaIdsMap = createMapOfSet(contactsPersonaIds);

        const allPersonaEmails = contactsPersonaIds
            .map(personaId => contactsPersonas[personaId])
            .concat(favoritePersonas)
            .filter(
                persona =>
                    isHubPerson(persona) &&
                    persona.info.allEmailAddresses &&
                    persona.info.allEmailAddresses.length > 0
            )
            .map((persona: Person) => persona.info.allEmailAddresses)
            .reduce((result, value, index) => {
                return result.concat(value);
            }, []);
        const allPersonaEmailsMap = createMapOfSet(allPersonaEmails);

        return suggestedPersonas
            .filter(
                (persona: Person) =>
                    !allPersonaIdsMap[persona.hubPersonaId] &&
                    !persona.info.allEmailAddresses.some(email => allPersonaEmailsMap[email])
            )
            .map(toPersonaListElement);
    }
);

export const getSuggestedGroups = createSelector<
    AppState,
    HubState,
    UnifiedGroupsMap,
    string,
    string[],
    string[],
    FavoriteEntity[],
    PersonaEntry[]
>(
    'getSuggestedGroups',
    [
        appState => appState.data.groupsHubState.unifiedGroupsMap,
        appState => appState.data.userConfiguration.settings.UserCulture,
        appState => appState.data.groupsHubState.joinedGroupsIds,
        appState => appState.data.groupsHubState.ownedGroupsIds,
        appState => appState.data.hubState.favoritePersonsState.favoritePersons,
    ],
    (unifiedGroups, culture, joinedGroupsIds, ownedGroupsIds, favoritePersonas) => {
        const joinedGroup: UnifiedGroup[] = joinedGroupsIds.map(key => unifiedGroups[key]);
        const ownedGroup: UnifiedGroup[] = ownedGroupsIds.map(key => unifiedGroups[key]);
        const suggestedGroups = [...ownedGroup, ...joinedGroup]
            .filter(group => !getMatchingFavoriteUnifiedGroup(group.hubPersonaId, favoritePersonas))
            .sort(compareByGivenRelevancyAndRelevancy(culture))
            .map(toPersonaListElement);
        return suggestedGroups;
    }
);

export const getContactOrContactListEntries = createSelector<
    AppState,
    HubState,
    HubPersona[],
    string[],
    PersonaListViewModel,
    string,
    PersonaListEntry[]
>(
    'getContactsOrContactListEntries',
    [
        appState =>
            getPersonasByFolderId(
                appState,
                appState.data.contactFoldersState.rootContactFolder?.id ||
                    rootContactFolderDistinguishedName
            ),
        (_appState: AppState, hubState: HubState) =>
            hubState?.view ? hubState.view.deletePersonaState.personasBeingDeleted : [],
        getPersonaListViewModel,
        (appState: AppState) => appState.data.userConfiguration.settings.UserCulture,
    ],
    (contactsInRootFolder, personasBeingDeleted, personaListViewModel, culture) => {
        const entries = filterPersonasAndRemoveDeleted(
            contactsInRootFolder,
            personaListViewModel,
            personasBeingDeleted
        );

        return applyGroupingAndSorting(entries, personaListViewModel, culture);
    }
);

const getSearchEntries = createSelector<
    AppState,
    HubState,
    ContactFolder,
    SearchQuery,
    SearchState,
    HubPersonasMap,
    UnifiedGroupsMap,
    PersonaListViewModel,
    string,
    string[],
    PersonaListEntry[]
>(
    'getSearchEntries',
    [
        (appState: AppState) => appState.data.contactFoldersState.rootContactFolder,
        (appState: AppState) => appState.view.navigationState.currentQuery,
        (appState: AppState) => appState.data.searchState,
        createSelectorCreator(defaultMemoize, isPersonaMapsEqual)(
            appState => appState.data.personas,
            personas => personas
        ),
        (appState: AppState) => appState.data.groupsHubState.unifiedGroupsMap,
        getPersonaListViewModel,
        (appState: AppState) => appState.data.userConfiguration.settings.UserCulture,
        (_appState: AppState, hubState: HubState) =>
            hubState?.view ? hubState.view.deletePersonaState.personasBeingDeleted : [],
    ],
    (
        rootContactFolder,
        currentQuery,
        searchState,
        personas,
        groups,
        personaListViewModel,
        culture,
        personasBeingDeleted
    ) => {
        if (!currentQuery) {
            return [];
        }

        const allPersonas = Object.keys(personas).map(key => personas[key]);

        const { searchStateHasResults, backendResults } = extractSearchState(
            searchState,
            currentQuery
        );
        const searchResultsWithRelevancy =
            rootContactFolder && currentQuery.folderId === rootContactFolder.id
                ? mergeAndExpandResults(
                      personas,
                      groups,
                      backendResults,
                      searchStateHasResults,
                      searchPeopleLocally(
                          {
                              queryDataType: 'BasicQueryData',
                              queryString: currentQuery.queryText,
                              rootContactFolderId: rootContactFolder.id,
                          },
                          allPersonas
                      )
                  )
                : extractPersonasWithRelevancyScore(
                      personas,
                      groups,
                      searchState.searchResults[currentQuery.queryText]
                  );

        const filteredPersonas = filterPersonasAndRemoveDeleted(
            searchResultsWithRelevancy,
            personaListViewModel,
            personasBeingDeleted
        );

        const filteredPersonaExcludingDeletedGroups = removeDeletedGroups(filteredPersonas, groups);

        return applyGroupingAndSorting(
            filteredPersonaExcludingDeletedGroups,
            personaListViewModel,
            culture
        );
    }
);

/*
 * Returns the content of the personaList, sorted and grouped
 */
export const getGroupedPersonaListEntries = createSelector<
    AppState,
    HubState,
    AppState,
    HubState,
    HubPersonasMap,
    PersonaListViewModel,
    string,
    ClickedHubPersonaState,
    SearchQuery,
    string,
    boolean,
    boolean,
    boolean,
    DeletedItemsSubViewType,
    boolean,
    ContactFolder,
    PersonaListEntry[]
>(
    'getGroupedPersonaListEntries',
    [
        appState => appState,
        (_appState, hubState) => hubState,
        createSelectorCreator(defaultMemoize, isPersonaMapsEqual)(
            appState => appState.data.personas,
            personas => personas
        ),
        getPersonaListViewModel,
        getActiveLeftNavItemId,
        (appState: AppState) => appState.view.navigationState.clickedHubPersona,
        (appState: AppState) => appState.view.navigationState.currentQuery,
        (appState: AppState) => appState.data.userConfiguration.settings.UserCulture,
        isInSearchMode,
        (appState: AppState) => appState.view.navigationState.isNavigateBackAvailable,
        isInEVNavigationMode,
        (_appState: AppState, hubState: HubState) =>
            hubState?.view && hubState.view.deletedItemsFolderState.deletedItemsSubViewType,
        isInDeepLinkMode,
        getActiveLeftNavItemSparseFolder,
    ],
    (
        appState,
        hubState,
        personas,
        personaListViewModel,
        activeLeftNavItemId,
        clickedHubPersona,
        currentQuery,
        culture,
        inSearchMode,
        isNavigateBackAvailable,
        isInEVNavigationMode,
        deletedItemsSubViewType,
        isInDeepLinkMode,
        sparseFolder
    ) => {
        if (sparseFolder) {
            return []; // For sparse folders, we use getGroupedSparsePersonaListEntries
        }

        if (!!clickedHubPersona && !!clickedHubPersona.hubPersonaId && isInDeepLinkMode) {
            const persona =
                personas[clickedHubPersona.hubPersonaId] ||
                appState.data.groupsHubState.unifiedGroupsMap[clickedHubPersona.hubPersonaId];
            return applyGroupingAndSorting([persona], personaListViewModel, culture);
        } else if (isInEVNavigationMode && isNavigateBackAvailable) {
            // Navigation comes from EV
            return applyGroupingAndSorting(
                [appState.data.hubState.currentNavigationPersonaState.currentPersona],
                personaListViewModel,
                culture
            );
        } else if (currentQuery?.queryText) {
            return getSearchEntries(appState, hubState);
        } else if (
            clickedHubPersona &&
            inSearchMode &&
            clickedHubPersona.origin !== 'personaList'
        ) {
            // A person has been clicked outside of the personaList. Display only that person
            const persona =
                personas[clickedHubPersona.hubPersonaId] ||
                appState.data.groupsHubState.unifiedGroupsMap[clickedHubPersona.hubPersonaId];
            return applyGroupingAndSorting([persona], personaListViewModel, culture);
        } else if (activeLeftNavItemId === favoritesLeftNavItemId) {
            return getFavoritePersonas(appState, hubState);
        } else if (isActiveLeftNavItemDeletedItems(appState)) {
            const { deletedPersonasState } = appState.data.hubState;
            const personasToGroup =
                deletedItemsSubViewType === DeletedItemsSubViewType.DeletedContactsFolder
                    ? deletedPersonasState.deletedPersonas
                    : deletedPersonasState.recoverablePersonas;
            return applyGroupingAndSorting(personasToGroup, personaListViewModel, culture);
        } else if (isContactsOrContactListsLeftNavItem(activeLeftNavItemId)) {
            return getContactOrContactListEntries(appState, hubState);
        } else {
            return getCurrentFolderPersonas(appState, hubState);
        }
    }
);

/*
 * Returns the content of the personaList for a sparse folder. The list is a dictionary.
 */
export const getGroupedSparsePersonaListEntries = createSelector<
    AppState,
    ContactFolder,
    SparseFolderStateMap,
    HubPersonasMap,
    SparsePersonaListEntries
>(
    'getGroupedSparsePersonaListEntries',
    [
        getActiveLeftNavItemSparseFolder,
        (appState: AppState) => appState.data.contactFoldersState.sparseFoldersState,
        createSelectorCreator(defaultMemoize, isPersonaMapsEqual)(
            appState => appState.data.personas,
            personas => personas
        ),
    ],
    (activeLeftNavItemSparseFolder, sparseFolders, personas) => {
        const loaded: SparseFolderLoadedState =
            activeLeftNavItemSparseFolder &&
            sparseFolders[activeLeftNavItemSparseFolder.id] &&
            sparseFolders[activeLeftNavItemSparseFolder.id].loaded;

        const entries: SparsePersonaListEntries = {};
        loaded &&
            Object.keys(loaded).forEach(key => {
                entries[key] = toPersonaListElement(personas[loaded[key]]);
            });

        return entries;
    }
);

/*
 * Returns the first item from the contact list
 */
export const getFirstHubPersonaIdInPersonaList = createSelector<
    AppState,
    HubState,
    PersonaListEntry[],
    SparsePersonaListEntries,
    string
>(
    'getFirstHubPersonaIdInPersonaList',
    [getGroupedPersonaListEntries, getGroupedSparsePersonaListEntries],
    (personaListEntries, sparsePersonaListEntries) => {
        if (personaListEntries) {
            const firstEntry = personaListEntries.find(
                entry => entry.kind === HubListEntryKind.Content
            ) as PersonaEntry;

            if (firstEntry) {
                return firstEntry.value.hubPersonaId;
            }
        }

        if (sparsePersonaListEntries) {
            const sparseEntries = Object.values(sparsePersonaListEntries);
            const firstEntry =
                sparseEntries &&
                sparseEntries.find(entry => entry.kind === HubListEntryKind.Content);

            if (firstEntry) {
                return firstEntry.value.hubPersonaId;
            }
        }

        return null;
    }
);

/*
 * Returns the first item from the contact list
 */
export const getFirstHubPersonaInPersonaList = createSelector<
    AppState,
    HubState,
    PersonaListEntry[],
    HubPersona
>('getFirstHubPersonaInPersonaList', [getGroupedPersonaListEntries], personaListEntries => {
    if (!personaListEntries) {
        return null;
    }

    const firstEntry = personaListEntries.find(
        entry => entry.kind === HubListEntryKind.Content
    ) as PersonaEntry;
    return firstEntry ? firstEntry.value : null;
});

/**
 * Returns a value indicating whether to display results, a loading message or an error message
 */
export const getLoadingState = createSelector<
    AppState,
    HubState,
    PersonaListEntry[],
    SearchState,
    SearchQuery,
    string,
    boolean,
    boolean,
    boolean,
    FolderLoadingStateMap,
    ContactFolder,
    PersonaListLoadingState
>(
    'getLoadingState',
    [
        getGroupedPersonaListEntries,
        (appState: AppState) => appState.data.searchState,
        (appState: AppState) => appState.view.navigationState.currentQuery,
        getActiveLeftNavItemId,
        (appState: AppState, _hubState: HubState) =>
            appState.data.hubState.favoritePersonsState.areInitialFavoritesLoaded,
        (appState: AppState, _hubState: HubState) =>
            appState.data.hubState.deletedPersonasState.isError,
        (appState: AppState, _hubState: HubState) =>
            appState.data.hubState.deletedPersonasState.isLoading,
        (appState: AppState) => appState.data.contactFoldersState.folderLoadingState,
        (appState: AppState) => appState.data.contactFoldersState.rootContactFolder,
    ],
    (
        personaListEntries,
        searchState,
        currentQuery,
        activeLeftNavItemId,
        areInitialFavoritesLoaded,
        isDeletedPersonasError,
        isDeletedPersonasLoading,
        folderLoadingState,
        rootContactFolder
    ) => {
        if (personaListEntries && personaListEntries.length > 0) {
            // Always show results if we have personas to display
            return PersonaListLoadingState.Results;
        }

        // There are no elements to display, need to figure out if we should display "loading"/"error"
        if (currentQuery?.queryText) {
            return searchState.searchResults[currentQuery.queryText].isLoading
                ? PersonaListLoadingState.Loading
                : searchState.searchResults[currentQuery.queryText].isError
                ? PersonaListLoadingState.Error
                : PersonaListLoadingState.Results;
        }

        switch (activeLeftNavItemId) {
            case favoritesLeftNavItemId:
                return areInitialFavoritesLoaded
                    ? PersonaListLoadingState.Results
                    : PersonaListLoadingState.Loading;

            case deletedItemsLeftNavItemId:
                return getPersonaListLoadingState(isDeletedPersonasLoading, isDeletedPersonasError);

            default:
                const idLoading = isContactsOrContactListsLeftNavItem(activeLeftNavItemId)
                    ? rootContactFolder?.id
                    : activeLeftNavItemId;
                const loadingState = idLoading && folderLoadingState[idLoading];
                switch (loadingState) {
                    case undefined:
                        return PersonaListLoadingState.Loading;

                    case FolderLoadingState.Error:
                        return PersonaListLoadingState.Error;

                    case FolderLoadingState.InProgress:
                    case FolderLoadingState.Done:
                    default:
                        return PersonaListLoadingState.Results;
                }
        }
    }
);

export const getDisplayedItemHubPersonaIds = createSelector<
    AppState,
    HubState,
    PersonaListEntry[],
    string[]
>('getDisplayItemId', [getGroupedPersonaListEntries], entries => {
    return !entries || !entries.length
        ? []
        : entries
              .filter(entry => entry.kind === HubListEntryKind.Content)
              .map((entry: PersonaEntry) => entry.value.hubPersonaId);
});

export const getNextItemInList = createSelector<
    AppState,
    HubState,
    string,
    boolean,
    string,
    string[],
    string
>(
    'getNextItemInList',
    [
        (_appState, _hubState, groupHubPersonaId, _shouldFetchNextItemInSearch) =>
            groupHubPersonaId,
        (_appState, _hubState, _groupHubPersonaId, shouldFetchNextItemInSearch) =>
            shouldFetchNextItemInSearch,
        getActiveLeftNavItemId,
        getDisplayedItemHubPersonaIds,
    ],
    (groupHubPersonaId, shouldFetchNextItemInSearch, activeLeftNavId, displayedHubPersonaIds) => {
        if (
            (activeLeftNavId === favoritesLeftNavItemId || shouldFetchNextItemInSearch) &&
            displayedHubPersonaIds.length > 0
        ) {
            const currentIndex = displayedHubPersonaIds.findIndex(hubPersonaId => {
                return hubPersonaId === groupHubPersonaId;
            });
            return (
                displayedHubPersonaIds[currentIndex + 1] || displayedHubPersonaIds[currentIndex - 1]
            );
        } else {
            return undefined;
        }
    }
);
